import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators';

import { GoogleTagManagerFacade } from 'ish-core/facades/gtm.facade';

import { PresidioNotificationItem } from '../models/presidio-notification.model';
import { PresidioNotificationsService } from './presidio-notifications.service';

/**
 * CUSTOMIZATION: Header notifications (quote alert 3.2.7 + approvals / order / pricing /
 * home “Needs your attention” outcomes).
 * UI-facing facade — mock service is DEMO-ONLY behind this contract.
 */
@Injectable({ providedIn: 'root' })
export class PresidioNotificationsFacade {
  private service = inject(PresidioNotificationsService);
  private gtm = inject(GoogleTagManagerFacade);

  private readonly itemsSubject = new BehaviorSubject<PresidioNotificationItem[]>([]);
  private readonly loadedSubject = new BehaviorSubject(false);

  readonly items$: Observable<PresidioNotificationItem[]> = this.itemsSubject.asObservable().pipe(
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly unreadCount$: Observable<number> = this.items$.pipe(
    map(items => items.filter(i => !i.read).length),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly hasUnread$: Observable<boolean> = this.unreadCount$.pipe(
    map(n => n > 0),
    distinctUntilChanged()
  );

  /** Lazy-load demo feed once (header mounts). */
  async ensureLoaded(): Promise<void> {
    if (this.loadedSubject.value) {
      return;
    }
    const items = await firstValueFrom(this.service.list());
    this.itemsSubject.next(items);
    this.loadedSubject.next(true);
  }

  trackPanelOpen(): void {
    this.gtm.triggeringGTMEvent('notification_open', {
      location: 'header',
      unread_count: this.itemsSubject.value.filter(i => !i.read).length,
    });
  }

  async selectNotification(item: PresidioNotificationItem): Promise<void> {
    this.gtm.triggeringGTMEvent('notification_select', {
      location: 'header',
      category: item.category,
      // Opaque business id only — never names/emails
      notification_id: item.id,
    });
    const items = await firstValueFrom(this.service.markRead(item.id));
    this.itemsSubject.next(items);
  }

  async setRead(item: PresidioNotificationItem, read: boolean): Promise<void> {
    const items = await firstValueFrom(this.service.setRead(item.id, read));
    this.itemsSubject.next(items);
    this.gtm.triggeringGTMEvent('notification_set_read', {
      location: 'header',
      category: item.category,
      notification_id: item.id,
      read,
    });
  }

  async markAllRead(): Promise<void> {
    const items = await firstValueFrom(this.service.markAllRead());
    this.itemsSubject.next(items);
    this.gtm.triggeringGTMEvent('notification_mark_all_read', { location: 'header' });
  }

  async clearAll(): Promise<void> {
    const priorCount = this.itemsSubject.value.length;
    const items = await firstValueFrom(this.service.clearAll());
    this.itemsSubject.next(items);
    this.gtm.triggeringGTMEvent('notification_clear_all', {
      location: 'header',
      cleared_count: priorCount,
    });
  }
}
